import React, { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { AlertCircle, User, Activity, Lock, AlertTriangle, Phone } from 'lucide-react';
import { patientSchema, type PatientFormData } from '@/utils/validation';
import {
    Patient,
    BirthOutcome,
    getBirthOutcomeLabel,
} from '@/types/patient.types';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { getDetaliiFromCNP, formatDateForInput } from '@/utils/cnp';
import { LocalitateAutocomplete } from '@/components/common/LocalitateAutocomplete';
import { referenceService } from '@/services/referenceService';
import { Judet } from '@/types/reference.types';

interface PatientGeneralFormProps {
    defaultValues: PatientFormData;
    onSubmit: (data: PatientFormData) => Promise<void>;
    isLoading?: boolean;
    submitLabel?: string;
    isEditMode?: boolean;
}

export const PatientGeneralForm: React.FC<PatientGeneralFormProps> = ({
    defaultValues,
    onSubmit,
    isLoading = false,
    submitLabel = "Salvează și Continuă",
    isEditMode = false,
}) => {
    const { utilizator } = useAuth();
    const { toast } = useToast();
    const [activeTab, setActiveTab] = React.useState<string>('general');
    const [judete, setJudete] = React.useState<Judet[]>([]);
    const [isCNPPopulated, setIsCNPPopulated] = React.useState(false);
    const [isJudetFromCNP, setIsJudetFromCNP] = React.useState(false);

    // Check if user is maternity level
    const isMaternityUser = utilizator?.user_level_id === 1 || utilizator?.user_level?.id === 1;

    const form = useForm<PatientFormData>({
        resolver: zodResolver(patientSchema),
        mode: 'onChange',
        defaultValues: defaultValues,
    });

    // Load judete on mount
    React.useEffect(() => {
        const loadJudete = async () => {
            try {
                const data = await referenceService.getJudete();
                setJudete(data);
            } catch (error) {
                console.error('Failed to load judete:', error);
                toast({
                    title: 'Eroare',
                    description: 'Nu s-au putut încărca județele.',
                    variant: 'destructive'
                });
            }
        };
        loadJudete();
    }, [toast]);

    // Check if patient has CNP and set lock state
    React.useEffect(() => {
        const cnp = form.getValues('cnp');
        if (cnp && cnp.length === 13) {
            setIsCNPPopulated(true);
            const details = getDetaliiFromCNP(cnp);
            setIsJudetFromCNP(details ? !details.isCnpNou : false);
        } else {
            setIsCNPPopulated(false);
            setIsJudetFromCNP(false);
        }
    }, [form]);


    // Handler for CNP changes - auto-populate gender, birth date, and judet
    const handleCNPChange = (value: string) => {
        // Update the CNP field value
        form.setValue('cnp', value);

        // If CNP has 13 digits, try to extract details
        if (value.length === 13) {
            const details = getDetaliiFromCNP(value);
            if (details) {
                // Auto-populate gender (0 = female, 1 = male)
                form.setValue('gender', details.sex);

                // Auto-populate birth date
                const formattedDate = formatDateForInput(details.dateNastere);
                form.setValue('birth_date', formattedDate);

                if (!details.isCnpNou) {
                    // CNP normal - set and lock judet
                    if (details.id_judet) {
                        form.setValue('id_judet', details.id_judet);
                    }
                    if (details.JudetNastereRenv) {
                        form.setValue('county', details.JudetNastereRenv.denumire);
                    }
                    form.setValue('id_localitate', null);
                    form.setValue('locality', '');
                    setIsJudetFromCNP(true);
                } else {
                    // CNP_NOU - leave judet/localitate editable
                    setIsJudetFromCNP(false);
                }

                // Lock sex and birth date fields
                setIsCNPPopulated(true);

                toast({
                    title: 'Date detectate din CNP',
                    description: `Sex: ${details.sex === 1 ? 'Masculin' : 'Feminin'}, Data nașterii: ${formattedDate}${!details.isCnpNou ? `, Județ: ${details.JudetNastereRenv?.denumire || 'necunoscut'}` : ', Județ: selectați manual (CNP nou)'}`,
                });
            } else {
                // Invalid CNP, unlock fields
                setIsCNPPopulated(false);
                setIsJudetFromCNP(false);
            }
        } else {
            // CNP cleared or incomplete, unlock fields
            setIsCNPPopulated(false);
            setIsJudetFromCNP(false);
        }
    };

    const handleSubmit = async (data: PatientFormData) => {
        await onSubmit(data);
    };

    return (
        <Form {...form}>
            <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
                {isMaternityUser && (
                    <Alert className="mb-4">
                        <AlertTriangle className="h-4 w-4" />
                        <AlertTitle>Acces restricționat</AlertTitle>
                        <AlertDescription>
                            Ca utilizator de maternitate, puteți completa datele generale și medicale ale pacientului.
                        </AlertDescription>
                    </Alert>
                )}
                <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                    <TabsList className="grid w-full grid-cols-3">
                        <TabsTrigger value="general">
                            <User className="h-4 w-4 mr-1" />
                            Date generale
                        </TabsTrigger>
                        <TabsTrigger value="medical">
                            <Activity className="h-4 w-4 mr-1" />
                            Date medicale
                        </TabsTrigger>
                        <TabsTrigger value="contact">
                            <Phone className="h-4 w-4 mr-1" />
                            Date contact
                        </TabsTrigger>
                    </TabsList>

                    <TabsContent value="general" className="space-y-4 mt-4">
                        {/* Hospital selector for users without hospital_id */}
                        {!utilizator?.hospital_id && (
                            <FormField
                                control={form.control}
                                name="hospital_id"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Spital *</FormLabel>
                                        <Select
                                            onValueChange={(value) => field.onChange(parseInt(value))}
                                            value={field.value?.toString()}
                                        >
                                            <FormControl>
                                                <SelectTrigger>
                                                    <SelectValue placeholder="Selectați spitalul" />
                                                </SelectTrigger>
                                            </FormControl>
                                            <SelectContent>
                                                <SelectItem value="1">Spitalul Clinic de Urgență pentru Copii "Marie Curie"</SelectItem>
                                                <SelectItem value="2">Institutul Național pentru Sănătatea Mamei și Copilului</SelectItem>
                                                <SelectItem value="3">Spitalul Clinic de Copii "Dr. Victor Gomoiu"</SelectItem>
                                                <SelectItem value="4">Spitalul Clinic de Urgență pentru Copii Cluj-Napoca</SelectItem>
                                                <SelectItem value="5">Spitalul Clinic de Urgență pentru Copii "Sf. Maria" Iași</SelectItem>
                                            </SelectContent>
                                        </Select>
                                        <FormDescription>
                                            Selectați spitalul unde este înregistrat pacientul
                                        </FormDescription>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        )}

                        <div className="grid grid-cols-2 gap-4">
                            <FormField
                                control={form.control}
                                name="name"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Nume complet *</FormLabel>
                                        <FormControl>
                                            <Input placeholder="Ex: Popescu Ion" {...field} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="patient_code"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Cod pacient</FormLabel>
                                        <FormControl>
                                            <Input
                                                placeholder="Generat automat"
                                                {...field}
                                                disabled={isEditMode}
                                            />
                                        </FormControl>
                                        <FormDescription>
                                            {isEditMode
                                                ? 'Codul pacientului nu poate fi modificat'
                                                : 'Lasați gol pentru generare automată'}
                                        </FormDescription>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </div>

                        <div className="grid grid-cols-3 gap-4">
                            <FormField
                                control={form.control}
                                name="cnp"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>CNP (Cod Numeric Personal)</FormLabel>
                                        <FormControl>
                                            <Input
                                                placeholder="Ex: 1234567890123"
                                                maxLength={13}
                                                value={field.value}
                                                onChange={(e) => handleCNPChange(e.target.value)}
                                                onBlur={field.onBlur}
                                                name={field.name}
                                            />
                                        </FormControl>
                                        <FormDescription>
                                            Cod Numeric Personal - 13 cifre (opțional, detectare automată sex și data nașterii)
                                        </FormDescription>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <FormField
                                control={form.control}
                                name="case_id"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>ID caz Registry MCC</FormLabel>
                                        <FormControl>
                                            <Input
                                                placeholder="UUID generat automat"
                                                {...field}
                                                disabled
                                            />
                                        </FormControl>
                                        <FormDescription>
                                            ID unic pentru registry-ul european
                                        </FormDescription>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="external_id"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>ID foaie observație (MRN)</FormLabel>
                                        <FormControl>
                                            <Input
                                                placeholder="ID din alt sistem"
                                                {...field}
                                            />
                                        </FormControl>
                                        <FormDescription>
                                            ID din sistemul spitalului sau alt sistem
                                        </FormDescription>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <FormField
                                control={form.control}
                                name="birth_date"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel className="flex items-center gap-2">
                                            Data nașterii *
                                            {isCNPPopulated && <Lock className="h-3 w-3 text-muted-foreground" />}
                                        </FormLabel>
                                        <FormControl>
                                            <Input type="date" {...field} disabled={isCNPPopulated} />
                                        </FormControl>
                                        {isCNPPopulated && (
                                            <FormDescription>
                                                Blocat - extras din CNP
                                            </FormDescription>
                                        )}
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="gender"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel className="flex items-center gap-2">
                                            Sex *
                                            {isCNPPopulated && <Lock className="h-3 w-3 text-muted-foreground" />}
                                        </FormLabel>
                                        <Select
                                            onValueChange={(value) => field.onChange(parseInt(value))}
                                            value={field.value?.toString()}
                                            disabled={isCNPPopulated}
                                        >
                                            <FormControl>
                                                <SelectTrigger>
                                                    <SelectValue placeholder="Selectați sexul" />
                                                </SelectTrigger>
                                            </FormControl>
                                            <SelectContent>
                                                <SelectItem value="0">Feminin</SelectItem>
                                                <SelectItem value="1">Masculin</SelectItem>
                                            </SelectContent>
                                        </Select>
                                        {isCNPPopulated && (
                                            <FormDescription>Blocat - extras din CNP</FormDescription>
                                        )}
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <FormField
                                control={form.control}
                                name="id_judet"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel className="flex items-center gap-2">
                                            Județ
                                            {isJudetFromCNP && <Lock className="h-3 w-3 text-muted-foreground" />}
                                        </FormLabel>
                                        <Select
                                            onValueChange={(value) => {
                                                const judetId = parseInt(value);
                                                field.onChange(judetId);
                                                // Update county name for display
                                                const judet = judete.find(j => j.id_judet === judetId);
                                                if (judet) {
                                                    form.setValue('county', judet.denumire);
                                                }
                                                // Clear locality when judet changes
                                                form.setValue('id_localitate', null);
                                                form.setValue('locality', '');
                                            }}
                                            value={field.value?.toString()}
                                            disabled={isJudetFromCNP}
                                        >
                                            <FormControl>
                                                <SelectTrigger>
                                                    <SelectValue placeholder="Selectați județul" />
                                                </SelectTrigger>
                                            </FormControl>
                                            <SelectContent className="max-h-[300px]">
                                                {judete.map((judet) => (
                                                    <SelectItem key={judet.id_judet} value={judet.id_judet.toString()}>
                                                        {judet.denumire}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                        {isJudetFromCNP && (
                                            <FormDescription>
                                                Blocat - extras din CNP
                                            </FormDescription>
                                        )}
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="id_localitate"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Localitate</FormLabel>
                                        <FormControl>
                                            <LocalitateAutocomplete
                                                value={field.value}
                                                onChange={(idLocalitate, nume) => {
                                                    field.onChange(idLocalitate);
                                                    // Update locality name for display
                                                    if (nume) {
                                                        form.setValue('locality', nume);
                                                    }
                                                }}
                                                idJudet={form.watch('id_judet')}
                                                disabled={!form.watch('id_judet')}
                                            />
                                        </FormControl>
                                        <FormDescription>
                                            {!form.watch('id_judet') ? 'Selectați județul mai întâi' : 'Căutați și selectați localitatea'}
                                        </FormDescription>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </div>
                    </TabsContent>

                    <TabsContent value="medical" className="space-y-4 mt-4">
                        <Alert>
                            <AlertCircle className="h-4 w-4" />
                            <AlertDescription>
                                Datele medicale de bază ale pacientului la naștere
                            </AlertDescription>
                        </Alert>
                        <div className="grid grid-cols-2 gap-4">
                            <FormField
                                control={form.control}
                                name="birth_weight"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Greutate la naștere (g)</FormLabel>
                                        <FormControl>
                                            <Input
                                                type="number"
                                                placeholder="Ex: 3200"
                                                value={field.value ?? ''}
                                                onChange={(e) => field.onChange(e.target.value ? parseInt(e.target.value) : null)}
                                                onBlur={field.onBlur}
                                                name={field.name}
                                            />
                                        </FormControl>
                                        <FormDescription>
                                            Între 300g și 6000g
                                        </FormDescription>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="gestational_age"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Vârstă gestațională (săptămâni)</FormLabel>
                                        <FormControl>
                                            <Input
                                                type="number"
                                                placeholder="Ex: 38"
                                                value={field.value ?? ''}
                                                onChange={(e) => field.onChange(e.target.value ? parseInt(e.target.value) : null)}
                                                onBlur={field.onBlur}
                                                name={field.name}
                                            />
                                        </FormControl>
                                        <FormDescription>
                                            Între 22 și 43 săptămâni
                                        </FormDescription>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </div>

                        <FormField
                            control={form.control}
                            name="maternity"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Maternitate</FormLabel>
                                    <FormControl>
                                        <Input placeholder="Numele maternității" {...field} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        {/* Birth Outcome */}
                        <FormField
                            control={form.control}
                            name="birth_outcome"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Rezultat naștere</FormLabel>
                                    <Select
                                        onValueChange={(value) => field.onChange(parseInt(value))}
                                        value={field.value?.toString()}
                                    >
                                        <FormControl>
                                            <SelectTrigger>
                                                <SelectValue placeholder="Selectați rezultatul nașterii" />
                                            </SelectTrigger>
                                        </FormControl>
                                        <SelectContent>
                                            {Object.values(BirthOutcome).filter(v => typeof v === 'number').map(outcome => (
                                                <SelectItem key={outcome} value={outcome.toString()}>
                                                    {getBirthOutcomeLabel(outcome as BirthOutcome)}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                    </TabsContent>

                    <TabsContent value="contact" className="space-y-4 mt-4">
                        <Card>
                            <CardHeader>
                                <CardTitle className="text-lg">Date de contact</CardTitle>
                                <CardDescription>
                                    Informații despre părinți și contact
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="grid grid-cols-2 gap-4">
                                    <FormField
                                        control={form.control}
                                        name="mother_name"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel>Nume mamă</FormLabel>
                                                <FormControl>
                                                    <Input placeholder="Numele mamei" {...field} />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />

                                    <FormField
                                        control={form.control}
                                        name="father_name"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel>Nume tată</FormLabel>
                                                <FormControl>
                                                    <Input placeholder="Numele tatălui" {...field} />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                </div>

                                <FormField
                                    control={form.control}
                                    name="notes"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Observații</FormLabel>
                                            <FormControl>
                                                <Textarea
                                                    placeholder="Alte observații despre pacient"
                                                    className="min-h-[100px]"
                                                    {...field}
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            </CardContent>
                        </Card>
                    </TabsContent>
                </Tabs>

                <div className="flex justify-end space-x-2">
                    <Button type="submit" disabled={isLoading || !form.formState.isValid}>
                        {isLoading ? (
                            <>
                                <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                                Se salvează...
                            </>
                        ) : (
                            submitLabel
                        )}
                    </Button>
                </div>
            </form>
        </Form>
    );
};
